/**
 * Wohin gehört ein Tagebucheintrag **ohne** Station?
 *
 * ## Warum diese Frage ein eigenes Modul hat
 *
 * Ein Eintrag hängt normalerweise an einer Station: Die Anreicherung setzt
 * `station_ord` (geteilte Seite) bzw. `stop_id` (eigene Ansicht), sobald sie
 * weiß, wo das Telefon stand. Sie kann aber ausfallen — kein Netz im Parkhaus,
 * kein Standort, ein Eintrag aus dem Gespräch ohne Ortsangabe. Dann steht dort
 * `null`, und trotzdem ist der Eintrag irgendwo entstanden.
 *
 * Die Antwort darauf gab es bis zum 20.9.2026 nur an **einer** Stelle
 * (`zeitleiste.gliedern`) — und genau deshalb an allen anderen Stellen gar
 * nicht. Befund des Nutzers vom selben Tag, unterwegs auf Reise 19 in Turin:
 * Vier Einträge entstanden zu Fuß, während das Auto im Parkhaus ohne Netz
 * stand. Auf der geteilten Seite standen sie ordentlich im zweiten Kapitel —
 * im Tagebuch bot ihr Foto aber kein „Als Bild für …" an, die Automatik sah
 * sie nicht, und im Dialog standen sie nicht bei den Fotos ihrer Gegend.
 * Wörtlich: „Für das erste Kapitel konnte ich auswählen, für das zweite
 * Kapitel aber nicht."
 *
 * Vier Flächen, die dieselbe Frage stellen, brauchen **eine** Antwort. Zwei
 * Fassungen wären zwei Gelegenheiten, sie verschieden zu treffen — und beide
 * sähen für sich richtig aus.
 *
 * ## Die Regel
 *
 * **Hinter die letzte Station, an der zeitlich schon etwas passiert war.** Der
 * *Anker* einer Station ist der früheste ihrer eigenen Einträge; eine Station
 * ohne eigenen Eintrag hat keinen und kann deshalb auch nichts an sich ziehen
 * — ohne Eintrag ist nicht belegt, dass er dort schon war. Ein Eintrag vor
 * allen Ankern ist *heimatlos*: Er steht zeitlich vor allem, was belegt ist.
 *
 * Beispiel aus der echten Reise: „Mautstelle Brenner Süd" um 15:09 gehört
 * hinter Bernau (13:50) und vor Trento (16:45).
 *
 * ## Warum sie generisch über den Stationsschlüssel ist
 *
 * Dieselbe Reise wird an zwei Stellen mit zwei verschiedenen Schlüsseln
 * gezählt: Die geteilte Seite kennt Stationen über `ord` (Kennungen gehen dort
 * gar nicht hinaus), das Tagebuch des Besitzers über `stops.id`. Eine Fassung
 * je Schlüssel wäre wieder zweimal dieselbe Regel; ein Typparameter kostet
 * nichts.
 *
 * ## Warum hier und nicht in `zeitleiste.ts`
 *
 * `zeitleiste.ts` zieht `planner/abstecher` und `tagebuch` mit hinein. Die
 * Gegendrechnung (`kapitel.ts`) hält sich absichtlich von `planner/` fern —
 * siehe ihren Kopfkommentar —, und sie ist der zweite Aufrufer dieser Regel.
 * Ein eigenes Modul ohne Abhängigkeiten lässt beide darauf zeigen, ohne dass
 * ein Kreis entsteht.
 *
 * **Reine Funktionen** — kein DOM, kein Netz, keine Uhr.
 */

/** Was von einem Eintrag gebraucht wird — mehr nicht. */
export interface Heimateintrag {
  id: number;
  /**
   * ISO-Zeitstempel. Verglichen wird als Text — ISO sortiert sich selbst.
   *
   * Nullbar, weil die Aufrufer ihn verschieden streng typisieren (die
   * geteilte Seite bekommt ihn immer, die Tagebuchliste des Besitzers hat ihn
   * als optionales Feld). Fehlt er, gilt er als leer und liegt damit **vor**
   * jedem Anker: Ein Eintrag ohne Zeit hat keinen Platz in der Abfolge, und
   * ihn irgendwo einzuhängen wäre geraten.
   */
  ts?: string | null;
}

/** Der Zeitstempel als vergleichbarer Text — fehlend heißt „vor allem". */
function zeit(e: Heimateintrag): string {
  return e.ts ?? '';
}

/**
 * Je Station ihr **Anker**: der früheste eigene Eintrag.
 *
 * Stationen ohne eigenen Eintrag fehlen in der Karte. Das ist die Aussage und
 * kein Mangel: Sie können keinen Zwischenhalt an sich ziehen, weil nicht
 * belegt ist, dass er dort schon war.
 *
 * Steht hier neben der eigentlichen Regel und nicht darin, weil die Zeitleiste
 * sie einmal je Gliederung braucht und `heimatstationen` sie sonst je Aufruf
 * neu rechnete.
 */
export function ankerzeiten<K, E extends Heimateintrag>(
  eintraege: readonly E[],
  stationVon: (e: E) => K | null
): Map<K, string> {
  const raus = new Map<K, string>();
  for (const e of eintraege) {
    const k = stationVon(e);
    if (k == null) continue;
    const bisher = raus.get(k);
    if (bisher == null || zeit(e) < bisher) raus.set(k, zeit(e));
  }
  return raus;
}

/**
 * Je Eintrag die Station, in deren Block er gehört — `id` → Schlüssel.
 *
 * Auch die Einträge **mit** Station stehen darin, und zwar mit ihrer eigenen:
 * Der Aufrufer stellt eine Frage („wo gehört dieser Eintrag hin?") und soll
 * nicht vorher entscheiden müssen, welchen der beiden Wege sie nimmt. `null`
 * heißt: vor jeder belegten Station entstanden.
 *
 * `reihenfolge` sind **alle** Stationen der Reise in Reisereihenfolge. Die
 * Reihenfolge trägt die Aussage, nicht der Schlüssel selbst: Gesucht ist die
 * *letzte* Station mit einem Anker vor der Zeit des Eintrags. `ord` darf
 * Lücken haben (übersprungene Stationen), und `stops.id` sagt über die
 * Reihenfolge ohnehin nichts.
 *
 * Verglichen wird `anker <= ts`: Ein Eintrag in derselben Sekunde wie der
 * erste Eintrag einer Station gehört zu dieser Station und nicht zur
 * vorherigen. Irgendeine Regel muss es sein, und diese ist die, bei der ein
 * Eintrag so weit vorrückt, wie die Daten es hergeben.
 *
 * Gerechnet wird über **die übergebenen** Einträge. Das ist ausdrücklich so
 * gewollt und der Grund, warum die geteilte Seite und das Tagebuch des
 * Besitzers zu verschiedenen Antworten kommen dürfen: Dort zählen nur die
 * geteilten Einträge, hier alle. Wer eine Station nur in einem
 * zurückgehaltenen Eintrag erwähnt, hat sie für Mitleser nicht belegt — und
 * genau deshalb darf ihr Anker auf der geteilten Seite nicht gelten.
 */
export function heimatstationen<K, E extends Heimateintrag>(
  reihenfolge: readonly K[],
  eintraege: readonly E[],
  stationVon: (e: E) => K | null,
  anker?: ReadonlyMap<K, string>
): Map<number, K | null> {
  const anke = anker ?? ankerzeiten(eintraege, stationVon);
  const raus = new Map<number, K | null>();
  for (const e of eintraege) {
    const eigene = stationVon(e);
    if (eigene != null) {
      raus.set(e.id, eigene);
      continue;
    }
    let ziel: K | null = null;
    for (const k of reihenfolge) {
      const a = anke.get(k);
      if (a != null && a <= zeit(e)) ziel = k;
    }
    raus.set(e.id, ziel);
  }
  return raus;
}
